// src/features/dashboard/components/EmployeeCompliance/CompliancePolicyChart.tsx

import { useState } from "react";
import { ShieldCheck } from "lucide-react";

import {
  ResponsiveContainer,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  BarChart,
  Bar,
  Cell,
  ReferenceLine,
  LabelList,
} from "recharts";

import {
  PolicyChartItem,
  PolicyTableItem,
} from "../../types/Employee/employeeCompliance.types";

interface Props {
  data: PolicyChartItem[];
  tableData: PolicyTableItem[];
}

const SAFE_COLOR = "#22C55E";

const WARNING_COLOR = "#F59E0B";

const CompliancePolicyChart = ({
  data,
  tableData,
}: Props) => {

  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);

  const [hoveredLegend, setHoveredLegend] = useState<
  "safe" | "violation" | null
>(null);

const values = data.map((x) => x.minutes);

const threshold =
  data.length > 0
    ? data[0].threshold
    : 30;

  console.log("compilance Threshhold",threshold);

// include threshold while calculating axis
const allValues = [...values, threshold];

const minValue = Math.min(...allValues);
const maxValue = Math.max(...allValues);

// round to nearest 10 with padding
const yMin = Math.max(
  0,
  Math.floor((minValue - 10) / 10) * 10
);

const yMax =
  Math.ceil((maxValue + 10) / 10) * 10;

const ticks: number[] = [];

for (let i = yMin; i <= yMax; i += 10) {
  ticks.push(i);
}


    const chartData = data.map((item) => {
  const [day = "", date = ""] = item.day.split(" ");

  return {
    ...item,
    shortDay: day.substring(0, 3), // Mon
    date,
  };
});

  return (
    <div
      className="
        rounded-2xl
        border
        border-[#D9DEE8]
        bg-white
        p-5
      "
    >

      {/* Header */}

      <div className="mb-5 flex items-center justify-between">

        <div className="flex items-center gap-2">

          <div
            className="
              flex
              h-5
              w-5
              items-center
              justify-center
              rounded-full
              bg-[#15803D]
            "
          >
            <ShieldCheck
              size={11}
              color="#FFFFFF"
              strokeWidth={2.5}
            />
          </div>

          <h3 className="text-[15px] font-semibold text-[#002B5B]">
            Gate 4 · Policy Compliance
          </h3>

        </div>

        <div
          className="
            rounded-full
            bg-[#EAF2FF]
            px-3
            py-[5px]
            text-[12px]
            text-[#2563EB]
          "
        >
          Restricted Apps · Unauthorized Websites · WTD
        </div>

      </div>

  {/* Legend */}

<div className="mb-4 flex items-center justify-center gap-6">

  {/* Safe */}

  <div
    className="flex cursor-pointer items-center gap-2"
    onMouseEnter={() => setHoveredLegend("safe")}
    onMouseLeave={() => setHoveredLegend(null)}
  >
    <span
      className="h-3 w-3 rounded-[2px] transition-all"
      style={{
        backgroundColor: SAFE_COLOR,
        opacity:
          hoveredLegend === null
            ? 1
            : hoveredLegend === "safe"
            ? 1
            : 0.3,
      }}
    />

    <span
      className={`text-[13px] transition-all ${
        hoveredLegend === "safe"
          ? "font-semibold text-[#111827]"
          : "text-[#334155]"
      }`}
    >
      Safe
    </span>
  </div>

  {/* Violations */}

  <div
    className="flex cursor-pointer items-center gap-2"
    onMouseEnter={() => setHoveredLegend("violation")}
    onMouseLeave={() => setHoveredLegend(null)}
  >
    <span
      className="h-3 w-3 rounded-[2px] transition-all"
      style={{
        backgroundColor: WARNING_COLOR,
        opacity:
          hoveredLegend === null
            ? 1
            : hoveredLegend === "violation"
            ? 1
            : 0.3,
      }}
    />

    <span
      className={`text-[13px] transition-all ${
        hoveredLegend === "violation"
          ? "font-semibold text-[#111827]"
          : "text-[#334155]"
      }`}
    >
      Violations
    </span>
  </div>

</div>

            {/* Chart */}

      <div className="h-[190px]">

        <ResponsiveContainer
          width="100%"
          height="100%"
        >

      <BarChart
  data={chartData}
  margin={{
    top: 10,
    right: 24,
    left: 10,
    bottom: 10,
  }}
  barCategoryGap="40%"
>
  <CartesianGrid
    stroke="#EEF2F7"
    vertical={false}
  />

<XAxis
  dataKey="shortDay"
  interval={0}
  height={50}
  axisLine={false}
  tickLine={false}
  tickMargin={10}
  tick={({ x, y, payload }) => {
    const item = chartData.find(
      (d) => d.shortDay === payload.value
    );

    return (
      <g transform={`translate(${x},${y})`}>
        <text
          textAnchor="middle"
          fill="#475467"
          fontSize={11}
          fontWeight={600}
        >
          <tspan x="0" dy="0">
            {payload.value}
          </tspan>

          <tspan x="0" dy="14">
            {item?.date}
          </tspan>
        </text>
      </g>
    );
  }}
/>

<YAxis
  domain={[-10, 50]}
  ticks={[-10, 10, 30, 50]}
  width={45}
  axisLine={false}
  tickLine={false}
  tick={{
    fontSize: 12,
    fill: "#475467",
  }}
  label={{
    value: "Minutes",
    angle: -90,
    position: "insideLeft",
    offset: -4,
    style: {
      fill: "#111827",
      fontSize: 12,
      fontWeight: 600,
    },
  }}
/>

  <Tooltip
  cursor={false}
  content={({ active, payload, label }) => {
    if (!active || !payload?.length) return null;

    const item = payload[0].payload;

    return (
      <div className="rounded-lg border border-[#D0D5DD] bg-white px-4 py-3 shadow-lg">
        <div className="mb-2 text-[13px] font-semibold text-[#344054]">
          {label}
        </div>

        <div className="flex items-center gap-2">
          <span
            className="h-3 w-3 rounded-full"
            style={{
              backgroundColor:
                item.minutes > threshold
                  ? WARNING_COLOR
                  : SAFE_COLOR,
            }}
          />

          <span className="text-[13px] text-[#344054]">
            Usage:
          </span>

          <span className="text-[13px] font-semibold text-[#111827]">
            {item.minutes} min
          </span>
        </div>
      </div>
    );
  }}
/>

  <ReferenceLine
  y={threshold}
  stroke="#F87171"
  strokeDasharray="4 4"
  strokeWidth={1.5}
label={{
  value: `${threshold}m threshold`,
  position: "right",
  fill: "#EF4444",
  fontSize: 11,
  fontWeight: 600,
}}
/>

  <Bar
  dataKey="minutes"
  radius={[4, 4, 0, 0]}
  barSize={30}
  onMouseOver={(_, index) => setHoveredIndex(index)}
  onMouseOut={() => setHoveredIndex(null)}
>
  {data.map((item, index) => {
    const normalColor =
      item.minutes > 30
        ? "#FB923C"
        : "#4ADE80";

    const hoverColor =
      item.minutes > 30
        ? "#F97316"
        : "#22C55E";

    return (
    <Cell
  key={index}
  fill={
    hoveredLegend === "safe"
      ? item.minutes <= threshold
        ? hoveredIndex === index
          ? "#22C55E"
          : "#4ADE80"
        : "#22C55E" // faded orange
      : hoveredLegend === "violation"
      ? item.minutes > threshold
        ? hoveredIndex === index
          ? "#F97316"
          : "#FB923C"
        : "#F97316" // faded green
      : hoveredIndex === index
      ? hoverColor
      : normalColor
  }
  style={{
    transition: "fill 180ms ease",
  }}
/>
    );
  })}

  <LabelList
  dataKey="minutes"
  content={({ x, y, width, height, value }: any) => (
    <text
      x={x + width / 2}
      y={y + height / 2 + 4}
      textAnchor="middle"
      fill="#FFFFFF"
      fontSize={12}
      fontWeight={700}
    >
      {value}m
    </text>
  )}
/>
</Bar>
</BarChart>

        </ResponsiveContainer>

      </div>

      {/* Part 2 - Table goes here */}

            {/* Policy Compliance Table */}

      <div className="mt-6 overflow-hidden rounded-xl border border-[#E5E7EB]">

        <table className="w-full border-collapse">

          <thead>

            <tr className="border-b border-[#E5E7EB] bg-white">

              <th className="px-5 py-3 text-left text-[13px] font-semibold text-[#111827]">
                Day
              </th>

              <th className="px-5 py-3 text-center text-[13px] font-semibold text-[#111827]">
                Safe
              </th>

              <th className="px-5 py-3 text-center text-[13px] font-semibold text-[#111827]">
                Violations
              </th>

              <th className="px-5 py-3 text-center text-[13px] font-semibold text-[#111827]">
                Status
              </th>

            </tr>

          </thead>

          <tbody>

            {tableData.map(
              (
                row: PolicyTableItem,
                index: number,
              ) => (

                <tr
                  key={`${row.day}-${index}`}
                  className={`
                    border-b
                    border-[#EEF2F7]
                    ${
                      index === tableData.length - 1
                        ? "bg-[#DBEAFE]"
                        : "bg-white hover:bg-[#F8FAFC]"
                    }
                  `}
                >

                  <td className="px-5 py-[10px] text-[13px] text-[#111827]">
                    {row.day}
                  </td>

                  <td className="px-5 py-[10px] text-center text-[13px] font-medium text-[#111827]">
                    {row.safe}
                  </td>

                  <td className="px-5 py-[10px] text-center text-[13px] text-[#667085]">
                    {row.violations}
                  </td>

                  <td className="px-5 py-[10px] text-center">

                    <span
                      className={`
                        inline-flex
                        items-center
                        rounded-full
                        px-3
                        py-[3px]
                        text-[11px]
                        font-semibold
                        text-white
                        ${
                          row.status === "Met"
                            ? "bg-[#16A34A]"
                            : "bg-[#DC2626]"
                        }
                      `}
                    >
                      {row.status}
                    </span>

                  </td>

                </tr>

              ),
            )}

          </tbody>

        </table>

      </div>

    </div>
  );
};

export default CompliancePolicyChart;